import type { HelpMap } from './types';

/**
 * Pojęcia: symulacja, świat, organizm, mózg, ewolucja, ekologia.
 * Zasada: każde twierdzenie o GENESIS ma odpowiadać temu, co silnik naprawdę robi (docs/MECHANICS.md);
 * ciekawostki to sprawdzalne fakty z nazwiskiem lub rokiem.
 */
export const CONCEPTS: HelpMap = {
  // ————————————————————————————— SYMULACJA —————————————————————————————
  tick: {
    group: 'Symulacja',
    title: 'Tick (krok czasu)',
    short: 'Najmniejsza jednostka czasu w symulacji. W jednym ticku każdy organizm raz patrzy, decyduje, rusza się, je, ewentualnie atakuje, starzeje się i może się rozmnożyć.',
    more: [
      'Czas w GENESIS liczy się w tickach, nie w sekundach. Dzięki temu wynik nie zależy od mocy komputera ani od liczby klatek na sekundę: ten sam eksperyment da ten sam przebieg na laptopie i na serwerze.',
      'Typowy organizm żyje ok. 2–4 tys. ticków, a pokolenie trwa kilkaset ticków.',
    ],
    fun: 'Przy tempie 1× jeden milion ticków trwałby ponad 9 godzin. Na MAX milion ticków policzył się w testach autora w ok. 7 minut — to ok. 1600 pokoleń.',
    see: ['speed', 'determinism', 'generation'],
  },
  speed: {
    group: 'Symulacja',
    title: 'Tempo symulacji',
    short: '1× = 30 ticków na sekundę, 10× = 300, 100× = 3000, 1000× = 30 000. MAX liczy tak szybko, jak pozwala procesor.',
    more: [
      'Tempo zmienia tylko to, jak szybko oglądasz symulację — nie zmienia jej przebiegu. Świat liczy się w osobnym wątku (Web Worker), a ekran dostaje tylko migawki, więc interfejs nie przycina się nawet na MAX.',
      'Na bardzo wysokim tempie widzisz już głównie wykresy, nie pojedyncze organizmy: ewolucja dzieje się w tysiącach pokoleń.',
    ],
    try: 'Wciśnij klawisze 1–5, aby szybko zmieniać tempo. Spacja = start/pauza.',
    see: ['tick', 'determinism'],
  },
  play: {
    group: 'Symulacja',
    title: 'Start, pauza i kroki',
    short: 'Start/Pauza (spacja) uruchamia lub zatrzymuje czas. „Krok” wykonuje jeden tick, „+100” — sto.',
    more: ['Po zatrzymaniu możesz swobodnie przeglądać świat, klikać organizmy i czytać ich genomy — nic się nie zmienia. Kroków używaj, gdy chcesz zobaczyć, co dokładnie dzieje się w jednym ticku (np. moment ataku).'],
    try: 'Zatrzymaj symulację, kliknij organizm i naciśnij „.” kilka razy — obserwuj, jak zmieniają się wejścia i wyjścia sieci.',
    see: ['tick'],
  },
  determinism: {
    group: 'Symulacja',
    title: 'Determinizm i powtarzalność',
    short: 'Ta sama konfiguracja + ten sam seed + ta sama wersja silnika + te same polecenia = identyczny wynik, bit w bit.',
    more: [
      'To podstawa naukowego eksperymentu: możesz powtórzyć przebieg, zmienić jedną rzecz i mieć pewność, że różnica wynika z tej zmiany, a nie z przypadku. Dlatego interwencje użytkownika są zapisywane w dzienniku poleceń razem z numerem ticku.',
      'Żeby wynik nie zależał od komputera, silnik unika funkcji takich jak sin, cos czy exp (różne przeglądarki mogą liczyć je z minimalnie inną dokładnością) i używa tylko podstawowych działań oraz pierwiastka, których wynik jest ściśle określony standardem IEEE-754.',
    ],
    fun: 'Testy GENESIS przechodzą identycznie na macOS (Node 26) i Linuksie (Node 22) — wyniki wyewoluowane z tego samego seeda są takie same na obu systemach.',
    see: ['seed', 'prng', 'twin', 'commandlog'],
  },
  seed: {
    group: 'Symulacja',
    title: 'Seed (ziarno losowości)',
    short: 'Liczba, z której generator losowy tworzy cały „los” świata: rozmieszczenie żyznych plam, genomy założycieli, wyniki mutacji.',
    more: [
      'Zmiana seeda daje inny, ale tak samo skonstruowany świat — jak rzut kostką na nowo. To dlatego pojedynczy przebieg niewiele mówi: warto sprawdzić kilka seedów (zakładka Eksperyment → Seria seedów).',
      'Ten sam seed w innej wersji silnika da inny świat, bo reguły się zmieniły.',
    ],
    try: 'Utwórz dwa światy z tym samym seedem (przycisk „Bliźniak od startu”) — będą identyczne do chwili, gdy coś zmienisz w jednym z nich.',
    see: ['prng', 'twin', 'series'],
  },
  prng: {
    group: 'Symulacja',
    title: 'Generator liczb pseudolosowych',
    short: 'Program, który z ziarna wytwarza ciąg liczb wyglądający na losowy, ale w pełni przewidywalny.',
    more: ['GENESIS używa generatora sfc32. Ma kilka niezależnych strumieni: osobny dla świata, dla mapy żyzności, dla założycieli i dla dryfu plam — dzięki temu np. analiza statystyczna nie „zjada” losowości symulacji i nie zmienia jej przebiegu.'],
    fun: 'Rozkład normalny (dzwon Gaussa) do mutacji jest tu przybliżany sumą czterech liczb równomiernych — to działanie centralnego twierdzenia granicznego i pozwala uniknąć logarytmów oraz cosinusów.',
    see: ['seed', 'determinism', 'mutation'],
  },
  twin: {
    group: 'Symulacja',
    title: 'Bliźniak (świat o identycznym starcie)',
    short: 'Nowy świat z tym samym seedem i tą samą konfiguracją początkową, uruchomiony od ticku 0.',
    more: ['Bliźniak służy do eksperymentów A/B: dopóki w żadnym z dwóch światów nic nie zmienisz, ich przebiegi są identyczne. Zmień jedną rzecz (np. wyłącz drapieżników) tylko w jednym — różnica na wykresach to efekt tej zmiany.'],
    try: 'Utwórz bliźniaka, w jednym świecie wywołaj suszę, a następnie otwórz zakładkę Porównanie.',
    see: ['fork', 'determinism', 'ab'],
  },
  fork: {
    group: 'Symulacja',
    title: 'Rozwidlenie (kopia bieżącego stanu)',
    short: 'Kopiuje świat w obecnym momencie do nowej zakładki. Oba światy idą dalej niezależnie.',
    more: ['To pytanie „co by było, gdyby…”: zostawiasz oryginał, a w kopii wprowadzasz zmianę — katastrofę, inną temperaturę, wyłączenie mutacji — i porównujesz dalszy bieg wydarzeń.'],
    try: 'Poczekaj, aż populacja się ustabilizuje, rozwidl świat i w kopii uruchom katastrofę.',
    see: ['twin', 'save'],
  },
  save: {
    group: 'Symulacja',
    title: 'Zapis i wznowienie',
    short: 'Pełny stan świata (zasoby, organizmy, genealogia, statystyki) zapisuje się do pliku lub do pamięci przeglądarki. Wznowienie jest dokładne co do bitu.',
    more: [
      'Autozapis robi się co zadaną liczbę ticków i zachowuje trzy ostatnie stany każdego świata. Zapisy są kompresowane (gzip) i mają numer wersji formatu, więc stare pliki nie zostaną po cichu wczytane niezgodnie.',
      'Pliki .genesis działają też w trybie bez interfejsu (npm run headless) — możesz policzyć milion ticków na serwerze i obejrzeć wynik w przeglądarce.',
    ],
    fun: 'Test wznowienia sprawdza, że świat odtworzony z pliku i dalej liczony daje stan identyczny z takim, który nigdy nie był zapisany.',
    see: ['determinism', 'headless'],
  },
  headless: {
    group: 'Symulacja',
    title: 'Tryb headless',
    short: 'Uruchamianie silnika bez interfejsu i bez rysowania, np. na serwerze — tylko liczenie.',
    more: ['Silnik jest oddzielony od wyglądu: ten sam kod działa w przeglądarce, w Node i w teście. Tryb headless ma punkty kontrolne i wznawianie, więc nadaje się do przebiegów na całą noc.'],
    try: 'W terminalu: npm run headless -- --ticks 1000000 --seed 7 --out runs/exp1',
    see: ['save', 'seed'],
  },
  commandlog: {
    group: 'Symulacja',
    title: 'Dziennik poleceń',
    short: 'Lista wszystkich interwencji (zmiany parametrów, zdarzenia, dosypania) z numerem ticku, w którym je wykonano.',
    more: ['Dziennik pozwala odtworzyć eksperyment: seed + konfiguracja + dziennik = ten sam przebieg. Zmiany są stosowane między tickami, nigdy w środku.'],
    see: ['determinism'],
  },
  events: {
    group: 'Symulacja',
    title: 'Zdarzenia środowiskowe',
    short: 'Jednorazowe lub czasowe wstrząsy, którymi możesz „szturchnąć” świat: susza, obfitość, ochłodzenie, katastrofa i zaraza zasobów.',
    more: ['Zdarzenia to narzędzie badawcze: sprawdzasz, jak populacja reaguje na stres i jak szybko się odbudowuje. Siła i czas trwania są regulowane suwakami. Wszystkie trafiają do dziennika zdarzeń.'],
    fun: 'Wielkie wymierania w historii Ziemi (np. to sprzed ok. 66 mln lat) otwierały nisze dla nowych grup. W GENESIS możesz sprawdzić, czy po katastrofie przeżywają ci sami, którzy dominowali wcześniej.',
    see: ['event.drought', 'event.bloom', 'event.cold', 'event.cull', 'event.blight'],
  },
  'event.severity': {
    group: 'Symulacja',
    title: 'Siła zdarzenia',
    short: 'Skala wstrząsu od 0,05 do 1: o ile spowolnić wzrost, ochłodzić świat albo jaki odsetek organizmów lub zasobów zniszczyć.',
    more: ['Susza mnoży tempo wzrostu zasobów przez (1 − siła); obfitość przez (1 + 4·siła); ochłodzenie obniża temperaturę o 0,8·siła; katastrofa zabija losową część populacji równą sile; zaraza niszczy ten ułamek zasobów.'],
    see: ['events'],
  },
  'event.duration': {
    group: 'Symulacja',
    title: 'Czas trwania zdarzenia',
    short: 'Przez ile ticków trwa susza, obfitość lub ochłodzenie. Katastrofa i zaraza są natychmiastowe.',
    more: ['Zdarzenie narasta i wygasa płynnie na początku i na końcu (ok. ćwierć czasu trwania, maks. 50 ticków), więc nie ma gwałtownych skoków w równaniach.'],
    see: ['events'],
  },
  'event.drought': {
    group: 'Symulacja',
    title: 'Susza',
    short: 'Przez zadany czas zasoby odnawiają się wolniej. Populacja głodnieje, a wygrywają oszczędni.',
    more: ['Po suszy często zostaje mniej, ale zwykle „twardszych” organizmów — to prosty obraz selekcji na odporność. Sprawdź wykres cech po suszy: co się zmieniło?'],
    try: 'Wywołaj suszę o sile 0,8 na 3000 ticków i obserwuj wykres „Populacja i demografia” oraz przyczyny zgonów (głód).',
    see: ['events', 'selection', 'bottleneck'],
  },
  'event.bloom': {
    group: 'Symulacja',
    title: 'Obfitość',
    short: 'Przez zadany czas zasoby odnawiają się znacznie szybciej. Populacja puchnie, a konkurencja słabnie.',
    more: ['Obfitość jest odwrotnością suszy: koszty życia są te same, ale pokarmu jest więcej. Zwykle po niej następuje gwałtowny przyrost liczby organizmów, a potem „kac” — gdy zasoby wracają do normy, populacja spada.'],
    fun: 'Takie cykle „boom–bust” znają ekolodzy z natury, np. z wahań liczebności lemingów.',
    see: ['events', 'carrying'],
  },
  'event.cold': {
    group: 'Symulacja',
    title: 'Ochłodzenie',
    short: 'Spada temperatura. Organizmy bez odporności na zimno spalają więcej energii i tracą zdrowie.',
    more: ['Odporność na zimno jest genem, który kosztuje (stałe utrzymanie izolacji), więc w ciepłym świecie jest „zbędna”. Ochłodzenie to test: czy populacja zdąży ją wyewoluować, zanim wymrze?'],
    see: ['temperature', 'trait.coldTolerance'],
  },
  'event.cull': {
    group: 'Symulacja',
    title: 'Katastrofa (masowe wymieranie)',
    short: 'Natychmiast ginie losowa część populacji — tyle, ile wynosi siła zdarzenia. Ich energia wraca do gleby jako zwłoki.',
    more: ['Katastrofa jest losowa — nie wybiera słabych, tylko wszystkich po równo. To czyste „wąskie gardło” populacji: przeżyją przypadkowi, a wraz z nimi ich geny. Mocna katastrofa może zmienić, które linie przetrwają, bez żadnego związku z ich przystosowaniem.'],
    see: ['bottleneck', 'drift', 'founder'],
  },
  'event.blight': {
    group: 'Symulacja',
    title: 'Zaraza zasobów',
    short: 'Natychmiast znika część pokarmu w całym świecie. Organizmy zostają, ale jest im czego jeść mniej.',
    more: ['W odróżnieniu od katastrofy zaraza uderza w zasoby, nie w organizmy: najpierw spada pokarm, a dopiero potem — przez głód — populacja.'],
    see: ['events'],
  },
  addfood: {
    group: 'Symulacja',
    title: 'Dosypanie zasobów',
    short: 'Ręcznie dodajesz energię do pokarmu — rozłożoną równomiernie po całym świecie.',
    more: ['To jedyne miejsce, gdzie energia pojawia się „z zewnątrz” poza naturalnym wzrostem. Jest księgowana osobno (wpływ „ręczny”), więc bilans energii nadal się zgadza.'],
    see: ['ledger'],
  },

  // ————————————————————————— ŚWIAT I ŚRODOWISKO —————————————————————————
  world: {
    group: 'Świat i środowisko',
    title: 'Świat 2D',
    short: 'Prostokąt 800×800 jednostek z twardymi ścianami (organizm odbija się od nich). Pod spodem leży siatka komórek 16×16 z pokarmem.',
    more: ['Świat jest jedynie sceną: wszystkie prawa (koszty, wzrost, atak) są jawne i opisane w dokumentacji. Rozmiar świata, liczbę założycieli i wzór żyzności ustawiasz przy tworzeniu nowego eksperymentu.'],
    see: ['resources', 'fertility'],
  },
  resources: {
    group: 'Świat i środowisko',
    title: 'Zasoby (pokarm)',
    short: 'Energia zmagazynowana w komórkach siatki. Rośnie sama (jak roślinność) i znika, gdy organizmy ją zjadają. Jasna zieleń na planszy = dużo pokarmu.',
    more: ['Zasoby są jedynym źródłem energii w świecie. Każdy organizm płaci koszty życia z tego, co zjadł — albo z tego, co odebrał innemu organizmowi.'],
    try: 'Włącz i wyłącz chip „Zasoby” nad planszą, żeby zobaczyć sam układ organizmów.',
    see: ['logistic', 'fertility', 'ledger'],
  },
  fertility: {
    group: 'Świat i środowisko',
    title: 'Żyzność',
    short: 'Mapa mówiąca, ile pokarmu może pomieścić każda komórka i jak szybko jej biomasa rośnie. Ciemne tło na planszy to żyzność; jaśniejsze plamy = żyźniejsze miejsca.',
    more: ['Domyślnie żyzność tworzą rozmyte plamy o losowym położeniu — poza nimi jest niemal pustynia. Wzór (plamy, gradient, oaza, jednolity) wybierasz przy tworzeniu świata.'],
    see: ['patches', 'resources'],
  },
  logistic: {
    group: 'Świat i środowisko',
    title: 'Wzrost logistyczny',
    short: 'Biomasa rośnie szybko, gdy jest jej średnio, wolno gdy prawie nic nie zostało i zatrzymuje się przy pojemności komórki.',
    more: [
      'W GENESIS przyrost to tempo · (biomasa + „nasiona”) · (1 − biomasa/pojemność). Gdy organizmy wyjedzą komórkę do zera, odrasta ona wolno — dlatego opłaca się nie przeżerać i szukać nietkniętych miejsc.',
      'Wcześniejsza wersja symulacji miała stałe źródło niezależne od biomasy: wszystko było wyjadane równomiernie i sensory nic nie dawały. Zmiana modelu zmieniła wyniki ewolucji — to dobry przykład, jak ważne jest prawo ekologiczne, na którym stoi cały świat.',
    ],
    fun: 'Równanie logistyczne opublikował w 1838 r. belgijski matematyk Pierre-François Verhulst. Do dziś opisuje wzrost populacji, której zasoby są ograniczone.',
    see: ['param.regrowthRate', 'param.foodSeed', 'carrying'],
  },
  patches: {
    group: 'Świat i środowisko',
    title: 'Plamy żyzności',
    short: 'Żyzne obszary o miękkich brzegach. Mogą dryfować po świecie i odradzać się w innych miejscach.',
    more: ['Dryf i cykl życia plam sprawiają, że „dobre miejsce” nie jest stałe. Można by się spodziewać, że premiuje to daleki wzrok i pamięć. W testach autora (3 seedy, 80 tys. ticków) tak nie było: zasięg zmysłów spadł do minimum, a pamięć nie dawała przewagi — przy tak wolnym dryfie (0,15) w porównaniu z szybkością organizmów wystarcza lokalne podążanie po gradiencie. Zobacz lekcję „Hipoteza, która nie przeszła”.'],
    try: 'Wybierz scenariusz „Wędrujące plamy”, porównaj z domyślnym i uruchom test ablacji. Spróbuj też szybszego dryfu (np. 1,0) — nie ma jeszcze pomiarów.',
    see: ['param.patchDrift', 'param.patchLife', 'trait.sense', 'memory'],
  },
  ledger: {
    group: 'Świat i środowisko',
    title: 'Bilans energii',
    short: 'Księgowość świata: energia w pokarmie + energia w organizmach = energia początkowa + wpływy − rozproszone ciepło. Różnica powinna wynosić zero.',
    more: [
      'Energia nie bierze się znikąd. Jedyne wpływy to wzrost zasobów („słońce”) i ręczne dosypanie. Wszystko inne to przepływ lub strata: metabolizm, ruch, sensory, mózg, jedzenie, atak, straty trawienia, narzut rozrodu, część zwłok i zanik nadmiaru pokarmu — każda strata trafia do osobnej rubryki jako ciepło.',
      'Wskaźnik „Niezgodność bilansu” w statystykach to automatyczna kontrola poprawności modelu. Po milionie ticków wynosi ok. 10⁻⁵ przy łącznym przepływie energii rzędu 10⁷ — czyli tyle, ile da błąd zaokrągleń.',
    ],
    fun: 'To ta sama zasada, co w fizyce — zachowanie energii. Gdyby w modelu pojawiła się energia „z niczego”, organizmy mogłyby wyewoluować, żeby ją wyzyskać, i wynik byłby artefaktem programu, a nie biologii.',
    see: ['metabolism', 'resources'],
  },
  temperature: {
    group: 'Świat i środowisko',
    title: 'Temperatura i zimno',
    short: '1,0 to komfort. Poniżej organizmy płacą więcej za metabolizm i tracą zdrowie — chyba że mają gen odporności na zimno.',
    more: ['Obciążenie zimnem to deficyt temperatury minus tolerancja organizmu (gen · 0,6). Mnożnik kosztów to 1 + K · obciążenie, a zdrowie spada proporcjonalnie do kwadratu obciążenia.'],
    see: ['seasons', 'trait.coldTolerance', 'event.cold'],
  },
  seasons: {
    group: 'Świat i środowisko',
    title: 'Pory roku',
    short: 'Okresowa „zima”: temperatura i tempo wzrostu zasobów spadają, potem wracają. Okres ustawiasz w parametrach.',
    more: ['Pory roku są falą trójkątną (bez trygonometrii, dla determinizmu). Premiują zapas energii (duże ciała), odporność na zimno i takie tempo rozrodu, które nie wpada w pułapkę zimy.'],
    try: 'Scenariusz „Pory roku” przy tworzeniu świata albo lekcja „Zima nadchodzi”. W testach autora (4 seedy, 30 tys. ticków) drapieżników było przy tym mniej: 5% vs 22%.',
    see: ['temperature', 'trait.coldTolerance'],
  },
  carrying: {
    group: 'Świat i środowisko',
    title: 'Pojemność środowiska',
    short: 'Tyle organizmów, ile świat jest w stanie wyżywić. W GENESIS nie ustawiasz jej wprost — wynika z energii.',
    more: ['„Limit populacji” w parametrach to tylko bezpiecznik (żeby nie zabrakło pamięci). Naprawdę liczbę organizmów ogranicza pokarm: gdy jest go za mało, rośnie głód i spada liczba urodzin.'],
    see: ['logistic', 'ledger', 'param.maxPopulation'],
  },
  corpse: {
    group: 'Świat i środowisko',
    title: 'Zwłoki i recykling',
    short: 'Energia zmarłego organizmu wraca do gleby jako pokarm (domyślnie 70%); reszta rozprasza się jako ciepło.',
    see: ['param.corpseRecycling', 'ledger'],
  },

  // ————————————————————————————— ORGANIZM —————————————————————————————
  organism: {
    group: 'Organizm',
    title: 'Organizm',
    short: 'Kropka z genomem, zapasem energii, zdrowiem i małym mózgiem. Nikt nim nie steruje — sam decyduje o skręcie, ruchu, jedzeniu i ataku.',
    more: ['Organizm widzi tylko to, co mieści się w jego zasięgu zmysłów, i na tej podstawie jego sieć neuronowa wylicza reakcję. Jego sukcesem jest liczba potomków, a nie zdobyte punkty.'],
    try: 'Kliknij dowolną kropkę, żeby otworzyć jej kartę: geny, fizjologię, sieć i rodowód.',
    see: ['genome', 'brain', 'fitness'],
  },
  genome: {
    group: 'Organizm',
    title: 'Genom',
    short: '289 liczb dziedziczonych po rodzicach: 16 cech fizycznych i 273 wag sieci neuronowej. To jedyne, co przechodzi na potomstwo.',
    more: [
      'Cechy mają wartości 0–1 i są przeliczane na wielkości fizyczne (np. gen rozmiaru 0,5 to promień 4). Wagi decydują o tym, jak mózg zamienia zmysły na działanie.',
      'Pasek genomu w karcie organizmu to jeden kolorowy prostokąt na gen: pierwsze to cechy, dalej wagi (turkus = dodatnia, czerwień = ujemna). W porównaniu z przodkiem żółta ramka oznacza gen, który się zmienił.',
    ],
    fun: 'Genom człowieka ma ok. 3 miliardy par zasad. Ten tutaj — 289 liczb. Mimo to wystarcza, by dobór naturalny wykształcił skręt w stronę pokarmu.',
    see: ['mutation', 'brain', 'inheritance'],
  },
  inheritance: {
    group: 'Organizm',
    title: 'Dziedziczenie',
    short: 'Potomek dostaje genom rodzica (lub dwojga rodziców) z drobnymi zmianami. Nic, czego organizm nauczył się w życiu, nie przechodzi na dzieci.',
    more: ['Pamięć krótkotrwała (3 komórki) zaczyna się u noworodka od zera — to dziedziczenie darwinowskie, nie lamarckowskie. Dziedziczy się tylko to, jak mózg jest „zbudowany”.'],
    fun: 'Jean-Baptiste Lamarck sądził w XIX w., że cechy nabyte (np. wyćwiczone mięśnie) przechodzą na potomstwo. Współczesna biologia nie potwierdza tego w klasycznej postaci — w GENESIS jest to wykluczone konstrukcją.',
    see: ['genome', 'memory', 'mutation'],
  },
  energy: {
    group: 'Organizm',
    title: 'Energia',
    short: 'Paliwo organizmu. Maleje przez koszty życia i ruchu, rośnie przez jedzenie. Zero oznacza śmierć głodową. Duże zapasy pozwalają się rozmnażać.',
    more: ['Maksymalna pojemność energii rośnie z rozmiarem jak masa^0,875 (od ok. 30 dla najmniejszych do ok. 200 dla największych). Rodzic w momencie rozrodu przekazuje potomkowi część energii, więc rozmnażanie jest realnym kosztem, nie darmowym „klonowaniem”.'],
    see: ['metabolism', 'ledger', 'reproduction'],
  },
  health: {
    group: 'Organizm',
    title: 'Zdrowie (kondycja)',
    short: 'Wartość 0–100%. Spada ze starością i od zimna, a odbudowuje się powoli, gdy organizm jest dobrze odżywiony. Niskie zdrowie osłabia ruch i apetyt.',
    more: ['Po 60% maksymalnego wieku zaczyna się starzenie: pułap zdrowia stopniowo spada aż do 30%. Zdrowie ≤ 0 oznacza śmierć. Zdrowie ogranicza też efektywną prędkość i wielkość kęsa (czynnik od 0,5 do 1).'],
    see: ['senescence', 'temperature'],
  },
  age: {
    group: 'Organizm',
    title: 'Wiek i długość życia',
    short: 'Maksymalny wiek zależy od rozmiaru: ok. 3000 ticków × masa^0,25. Większe organizmy żyją dłużej.',
    more: ['„Oczekiwana długość życia” w karcie organizmu to mniejsza z dwóch wartości: limit wieku oraz wiek, w którym organizm by zagłodził się bez jedzenia przy obecnych zapasach.'],
    fun: 'Zasada „większe żyją dłużej” jest w biologii ogólnym trendem (z licznymi wyjątkami); w GENESIS jest wpisana wprost jako masa^0,25.',
    see: ['senescence', 'kleiber'],
  },
  senescence: {
    group: 'Organizm',
    title: 'Starzenie',
    short: 'Po osiągnięciu 60% maksymalnego wieku pułap zdrowia organizmu zaczyna spadać, aż do 30% tuż przed końcem życia.',
    more: ['Starzenie jest w modelu deterministyczne — to nie „losowa śmierć”. Losowa jest osobno: wypadek (niewielkie prawdopodobieństwo na tick).'],
    see: ['health', 'age', 'death'],
  },
  generation: {
    group: 'Organizm',
    title: 'Pokolenie',
    short: 'Ile pokoleń dzieli organizm od jego założyciela. Założyciele to pokolenie 0, ich dzieci 1, wnuki 2…',
    more: ['Przy rozmnażaniu płciowym pokolenie dziecka to o jeden więcej niż większe z pokoleń rodziców. Liczba pokoleń jest właściwą miarą „czasu ewolucyjnego” — tysiąc ticków przy krótkim cyklu życia to znacznie więcej ewolucji niż przy długim.'],
    fun: 'W trwającym od 1988 r. eksperymencie Richarda Lenskiego bakterie E. coli przekroczyły 70 tys. pokoleń. W GENESIS tyle pokoleń wystarczy kilka godzin na MAX.',
    see: ['tick', 'evolution'],
  },
  metabolism: {
    group: 'Organizm',
    title: 'Metabolizm i koszty życia',
    short: 'Co tick organizm płaci za: podtrzymanie ciała, ruch, skręcanie, sensory, mózg, izolację przed zimnem, jedzenie i atak. Wszystko wraca do świata jako ciepło.',
    more: [
      'Podstawowy koszt rośnie z masą jak m^0,75 (prawo Kleibera), koszt ruchu z prędkością do kwadratu, koszt sensorów z kwadratem zasięgu, a koszt mózgu z sumą wartości bezwzględnych wag — im „silniejsza” sieć, tym droższa.',
      'Zasada przewodnia: nic nie jest za darmo. Każda zaleta ma cenę, więc nie istnieje jedna „najlepsza” konstrukcja organizmu dla wszystkich warunków.',
    ],
    see: ['kleiber', 'tradeoff', 'param.basalCoef'],
  },
  kleiber: {
    group: 'Organizm',
    title: 'Prawo Kleibera',
    short: 'Tempo przemiany materii rośnie z masą ciała wolniej niż sama masa — jak masa do potęgi 3/4.',
    more: ['Z tego powodu małe zwierzęta zużywają więcej energii na gram ciała niż duże. W GENESIS podstawowy koszt metabolizmu to właśnie współczynnik · masa^0,75.'],
    fun: 'Zależność opisał Max Kleiber w 1932 r. Serce myszy bije kilkaset razy na minutę, a słonia — kilkadziesiąt. Mysz żyje krócej, ale „pali” szybciej.',
    see: ['allometry', 'metabolism'],
  },
  allometry: {
    group: 'Organizm',
    title: 'Allometria (skalowanie z rozmiarem)',
    short: 'Różne cechy rosną z rozmiarem w różnym tempie — dlatego zmiana wielkości nigdy nie jest „neutralna”.',
    more: [
      'W GENESIS metabolizm i ruch rosną jak masa^0,75, pojemność energii jak masa^0,875, długość życia jak masa^0,25, a koszt potomka — liniowo z jego masą. Małe ciała szybciej się mnożą i mają niższy próg przeżycia; duże mają większe zapasy, dłuższe życie i przewagę w walce o pokarm.',
      'Bez drapieżników w żyznym świecie małe wygrywają i rozmiar spada do minimum. Z drapieżnikami (domyślnie) rozmiar utrzymuje się wyżej, bo presja jest dwustronna.',
    ],
    see: ['kleiber', 'tradeoff', 'trait.size'],
  },
  tradeoff: {
    group: 'Organizm',
    title: 'Kompromis ewolucyjny (trade-off)',
    short: 'Każda zaleta ma koszt: szybszy organizm płaci za ruch, ten z szerokim wzrokiem płaci za sensory, silny mózg żre energię.',
    more: ['Kompromisy sprawiają, że nie istnieje jedna najlepsza konstrukcja. Wynik zależy od środowiska: zimny świat nagrodzi izolację, świat pełen drapieżników — czujność i ucieczkę, bogaty w pokarm — szybkie rozmnażanie.'],
    fun: 'Ekolodzy nazywają to zasadą alokacji: organizm ma ograniczony budżet energii i musi go rozdzielić między wzrost, utrzymanie i rozród.',
    see: ['metabolism', 'niche'],
  },
  death: {
    group: 'Organizm',
    title: 'Przyczyny śmierci',
    short: 'Głód (brak energii na koszty), drapieżnik, zdrowie ≤ 0 (starość lub zimno), wypadek (losowy) i katastrofa (zdarzenie).',
    more: ['Wykres „Przyczyny zgonów” pokazuje, co faktycznie ogranicza populację. Głód dominuje, gdy brakuje pokarmu; zgony z rąk drapieżników rosną, gdy drapieżnictwo się utrzymuje; starość pojawia się, gdy populacja jest syta.'],
    see: ['predation', 'senescence'],
  },
  reproduction: {
    group: 'Organizm',
    title: 'Rozmnażanie',
    short: 'Organizm rozmnaża się, gdy jest dojrzały, ma dość energii i minęła karencja. Rodzic płaci inwestycję w potomka plus narzut jako ciepło.',
    more: [
      'Warunki: wiek ≥ dojrzałość (gen), co najmniej 40 ticków od poprzedniego porodu i energia wyższa od progu (gen) oraz od kosztu + 15% rezerwy. Potomek dostaje inwestycję (gen), więc „dużo małych” i „mało dużych” to dwie różne strategie.',
      'Uwaga: moment rozmnożenia wynika z energii, a nie z decyzji mózgu — to jedno z uproszczeń modelu.',
    ],
    see: ['trait.reproThreshold', 'trait.investment', 'trait.maturity', 'sexual'],
  },
  sexual: {
    group: 'Organizm',
    title: 'Rozmnażanie płciowe',
    short: 'Dwoje rodziców, rekombinacja genów. Potomek dostaje po neuronie ukrytym od jednego lub drugiego rodzica, a cechy — losowo od któregokolwiek.',
    more: [
      'Rekombinacja tasuje geny — dobra mutacja z jednego organizmu może spotkać się z dobrą mutacją z innego. W rozmnażaniu bezpłciowym, gdzie cały genom dziedziczy się razem, takie połączenie jest trudne.',
      'W GENESIS partner to najbliższy gotowy organizm, którego zaakceptują obaj rodzice przy włączonym wyborze partnera. Każdy może być rodzicem raz na tick. Obaj rodzice płacą po połowie kosztu.',
    ],
    fun: 'Biolodzy mówią o „podwójnym koszcie płci” (John Maynard Smith, 1978): organizmy płciowe wytwarzają dwa razy mniej potomstwa na osobnika niż klonujące się. W GENESIS obie „płci” są hermafrodytami, więc tego kosztu nie ma — ale też partner trzeba znaleźć.',
    try: 'Scenariusz „Rozmnażanie płciowe” — sprawdź wykres liczby kladów i różnorodności.',
    see: ['crossover', 'hitchhiking', 'clade'],
  },
  crossover: {
    group: 'Organizm',
    title: 'Rekombinacja neuron po neuronie',
    short: 'Dla każdego neuronu ukrytego potomek bierze komplet jego wag (wejściowych, bias i wyjściowych) od jednego z rodziców.',
    more: ['Gdyby tasować pojedyncze wagi, rozerwalibyśmy ustalone współdziałania i potomek byłby zwykle bezużyteczny. Neuron jako całość jest naturalną jednostką funkcjonalną sieci.'],
    fun: 'W neuroewolucji ten problem nazywa się „problemem permutacji”: ta sama funkcja może być zakodowana w sieci na wiele sposobów, więc łączenie dwóch sieci bywa destrukcyjne. Algorytm NEAT (Stanley i Miikkulainen, 2002) rozwiązuje to osobnym mechanizmem.',
    see: ['sexual', 'brain'],
  },

  // —————————————————————————————— MÓZG ——————————————————————————————
  brain: {
    group: 'Mózg',
    title: 'Sieć neuronowa (mózg)',
    short: 'Mała sieć 23 → 8 → 9: zmysły dwóch pokarmów, sąsiadów, sygnału i pamięci sterują ruchem, jedzeniem, atakiem, emisją i kultywacją. Plastyczność może zmieniać wagi w życiu organizmu.',
    more: [
      'Sieć liczy wszystko od nowa co tick: każdy neuron sumuje wejścia pomnożone przez wagi, dodaje bias i przepuszcza przez funkcję tanh, która ściska wynik do −1…1.',
      'Zachowanie wynika z odziedziczonych wag oraz ewentualnych zmian somatycznych; geny i pamięć mają oddzielny zapis. Jeśli organizm skręca w stronę pokarmu, to dlatego, że przodkowie z takimi wagami mieli więcej potomstwa.',
    ],
    fun: 'Nicień C. elegans ma 302 neurony i potrafi wędrować w stronę zapachu pokarmu. Sieć GENESIS ma 17 neuronów, a mimo to ewoluuje podobne odruchy.',
    try: 'W karcie organizmu włącz pauzę i patrz, jak jaśnieją węzły sieci, gdy organizm znajduje pokarm.',
    see: ['neuron', 'brain.diagram', 'notraining', 'memory'],
  },
  neuron: {
    group: 'Mózg',
    title: 'Neuron, waga, bias',
    short: 'Neuron sumuje swoje wejścia pomnożone przez wagi, dodaje bias i zwraca wartość z przedziału −1…1 (tanh).',
    more: ['Waga dodatnia wzmacnia sygnał, ujemna go hamuje. Bias to stała „skłonność” neuronu. Koszt utrzymania mózgu rośnie z sumą bezwzględnych wartości wszystkich wag — silne połączenia są drogie.'],
    see: ['brain'],
  },
  'brain.diagram': {
    group: 'Mózg',
    title: 'Jak czytać schemat sieci',
    short: 'Lewa kolumna: wejścia. Środek: neurony ukryte. Prawa: wyjścia. Turkusowe linie to wagi dodatnie, czerwone ujemne; grubość = siła. Kolor węzła = aktualna aktywacja.',
    more: ['Rysowane są tylko wagi o wartości bezwzględnej powyżej 0,25. Z diagramu widać np., czy wejście „jedzenie ←” silnie wpływa na wyjście „skręt” — tak działa wyewoluowana taksja.'],
    try: 'Wybierz organizm i patrz, które wejścia mają mocne (grube) połączenia z wyjściem „skręt”.',
    see: ['brain', 'inputs', 'outputs'],
  },
  inputs: {
    group: 'Mózg',
    title: 'Wejścia sieci (zmysły)',
    short: 'Energia, dwa pokarmy, widoczni sąsiedzi, ściany, temperatura, trzy komórki pamięci i lokalny sygnał. Pole widzenia i bariery mogą ograniczać odczyt wzrokowy.',
    more: [
      'Wszystko jest egocentryczne: „przód” i „lewo” liczone względem kierunku, w którym organizm patrzy. Kierunek każdego pokarmu to agregat widocznych komórek w zasięgu. Obrót i przeszkody zmieniają odczyt; sensory nadal nie rozpoznają pojedynczych obiektów.',
      'Rozmiar i dieta najbliższego sąsiada pozwalają, ale nie zmuszają, odróżnić potencjalnego drapieżnika od ofiary.',
    ],
    see: ['egocentric', 'trait.sense', 'brain'],
  },
  outputs: {
    group: 'Mózg',
    title: 'Wyjścia sieci (działania)',
    short: 'Skręt, ciąg, wysiłek jedzenia i ataku, trzy zapisy pamięci, podpisana emisja sygnału oraz wysiłek kultywacji.',
    more: ['Jedzenie i atak kosztują energię proporcjonalnie do wysiłku, więc sieć opłaca się „wyłączać” w pustce i włączać przy pokarmie lub ofierze. Atak działa tylko u organizmów z dietą powyżej progu.'],
    see: ['brain', 'trait.diet', 'predation'],
  },
  egocentric: {
    group: 'Mózg',
    title: 'Układ egocentryczny',
    short: 'Organizm opisuje świat „od siebie”: przód, lewo, prawo — nie północ i południe.',
    more: ['Dzięki temu proste połączenie „pokarm po lewej → skręć w lewo” działa niezależnie od tego, w którą stronę organizm akurat patrzy. Tak działa większość zwierzęcych zmysłów.'],
    see: ['inputs'],
  },
  memory: {
    group: 'Mózg',
    title: 'Pamięć rekurencyjna',
    short: 'Trzy liczby, które organizm sam zapisuje i które wracają na wejścia sieci w następnym ticku. Dzięki nim zachowanie może zależeć od przeszłości.',
    more: [
      'Bez pamięci sieć jest czystym odruchem: ta sama sytuacja zawsze daje tę samą reakcję. Z pamięcią organizm może np. pamiętać, że przed chwilą trafił na pokarm, albo że właśnie ucieka.',
      'Gen „trwałość pamięci” (λ od 0,5 do 0,99) określa, jak szybko stan zanika: co tick zostaje λ starej wartości i (1−λ) nowego zapisu. Przy λ = 0,5 pamięć zapomina niemal natychmiast, przy 0,99 połowa informacji znika po ok. 69 tickach.',
    ],
    fun: 'Taki układ nazywa się siecią Elmana (Jeffrey Elman, 1990). W GENESIS pamięć nie jest ani „zaprogramowanym” zachowaniem, ani gwarancją przewagi — test ablacji pokazuje, kiedy się opłaca.',
    try: 'Uruchom test ablacji w zakładce Statystyki po kilkudziesięciu tysiącach ticków i sprawdź wiersz „Pamięć rekurencyjna”.',
    see: ['ablation', 'trait.memory', 'inheritance'],
  },
  notraining: {
    group: 'Mózg',
    title: 'Ewolucja zamiast uczenia',
    short: 'Genom ewoluuje przez dziedziczenie, mutacje i rekombinację. Przy włączonej plastyczności efektywne wagi dodatkowo zmieniają się podczas życia, bez wstecznej propagacji.',
    more: ['To podejście nazywa się neuroewolucją. Dziedziczny sukces zależy od rozrodu; somatyczne uczenie wykorzystuje zmianę własnej energii. Nauczone wagi nie przechodzą na potomstwo.'],
    fun: 'Karl Sims w 1994 r. wyewoluował wirtualne stworzenia, które same nauczyły się pływać, chodzić i ścigać o sześcian — bez ręcznie napisanych zachowań. To był jeden z pierwszych głośnych pokazów neuroewolucji i ewolucji morfologii.',
    see: ['brain', 'fitness', 'evolution'],
  },
  reflex: {
    group: 'Mózg',
    title: 'Odruch vs planowanie',
    short: 'Odruch to stała reakcja na bodziec. Planowanie wymagałoby wyobrażenia przyszłości. GENESIS dowodzi co najwyżej odruchów, czasem z pamięcią.',
    more: ['Sondy zachowań mierzą tylko odruchy (np. „pokarm z lewej → skręt w lewo”). Nie ma tu dowodu na planowanie ani świadomość i żaden eksperyment tego nie deklaruje.'],
    see: ['probes', 'memory'],
  },

  // ————————————————————————————— EWOLUCJA —————————————————————————————
  evolution: {
    group: 'Ewolucja',
    title: 'Ewolucja przez dobór naturalny',
    short: 'Zachodzi, gdy są trzy rzeczy naraz: zmienność (mutacje), dziedziczność (potomek podobny do rodzica) i różny sukces rozrodczy.',
    more: ['GENESIS ma wszystkie trzy mechanicznie i nic ponadto: nie ma funkcji fitness, nikt nie ocenia organizmów. Ewoluują te geny, które znalazły się w większej liczbie potomków.', 'Dlatego wynik bywa zaskakujący — i dlatego trzeba go badać, a nie zakładać.'],
    fun: 'Charles Darwin opisał ten mechanizm w 1859 r. (O powstawaniu gatunków), nie znając genów. Wystarczyły mu trzy obserwacje: zmienność, dziedziczność i rywalizacja o ograniczone zasoby.',
    see: ['selection', 'mutation', 'fitness', 'emergence'],
  },
  fitness: {
    group: 'Ewolucja',
    title: 'Przystosowanie (fitness)',
    short: 'W biologii: ile potomstwa dany genotyp zostawia w kolejnych pokoleniach. W GENESIS nie ma żadnej funkcji punktującej organizmy.',
    more: ['W wielu algorytmach genetycznych programista ocenia osobniki wynikiem. Tu jedynym „wynikiem” jest faktyczne rozmnożenie się w świecie — dlatego nie można z góry zaplanować, jaka strategia wygra.'],
    see: ['evolution', 'selection'],
  },
  mutation: {
    group: 'Ewolucja',
    title: 'Mutacja',
    short: 'Przy narodzinach każdy gen z niewielkim prawdopodobieństwem (domyślnie 4%) zmienia się o losową wartość. Mutacje są jedynym źródłem nowej zmienności.',
    more: ['Przy 289 genach i mutacji 0,04 noworodek ma średnio ok. 12 zmian. Większość jest szkodliwa lub obojętna, rzadko któraś pomaga — i to ona rozprzestrzenia się dzięki dobremu rozmnażaniu.', 'Każda zmiana jest zapisywana w rejestrze mutacji i widoczna w genealogii.'],
    fun: 'U człowieka każde dziecko ma ok. 50–100 nowych mutacji (w porównaniu z genomami rodziców).',
    try: 'Ustaw częstość mutacji na 0 w jednym ze światów-bliźniaków i porównaj różnorodność.',
    see: ['param.mutationRate', 'param.mutationStrength', 'selection', 'drift'],
  },
  selection: {
    group: 'Ewolucja',
    title: 'Dobór naturalny',
    short: 'Organizmy, które lepiej radzą sobie w danych warunkach, zostawiają więcej potomstwa, więc ich geny stają się częstsze.',
    more: ['Dobór działa na organizmach, ale „widać” go w populacji: średnia wartość cechy przesuwa się w czasie. Wykres „Ewolucja cech” pokazuje dokładnie to.', 'Dobór nie ma celu i nie planuje: jest filtrem, który działa tu i teraz.'],
    try: 'W zakładce Statystyki znajdź wykres „Ewolucja cech” i sprawdź, które linie systematycznie rosną lub maleją.',
    see: ['evolution', 'fitness', 'tradeoff'],
  },
  drift: {
    group: 'Ewolucja',
    title: 'Dryf genetyczny',
    short: 'Losowe zmiany częstości genów, które nie mają związku z przystosowaniem. Silniejszy w małych populacjach.',
    more: ['Gdy potomstwo zostawia mało osobników, przypadek decyduje, kogo geny przetrwają. W małej populacji dryf potrafi usunąć dobre geny albo utrwalić złe.'],
    fun: 'W teście autora 25 założycieli dało po 15 tys. ticków różnorodność cech ok. 0,06, a 400 założycieli — ok. 0,16 (średnia z 6 seedów).',
    see: ['founder', 'bottleneck', 'hitchhiking'],
  },
  founder: {
    group: 'Ewolucja',
    title: 'Założyciele i efekt założyciela',
    short: 'Założyciele to organizmy z pokolenia 0 z losowymi genomami. Cała późniejsza zmienność pochodzi z ich genów i z mutacji.',
    more: ['Jeśli założycieli jest mało, od początku brakuje zmienności i populacja szybko staje się jednorodna. To tzw. efekt założyciela.'],
    try: 'Utwórz dwa światy z 25 i 400 założycielami i porównaj różnorodność po ok. 15 tys. ticków.',
    see: ['drift', 'diversity'],
  },
  bottleneck: {
    group: 'Ewolucja',
    title: 'Wąskie gardło populacji',
    short: 'Gwałtowny spadek liczebności (katastrofa, susza), po którym odradza się populacja z niewielkiej grupy ocalałych.',
    more: ['W teorii po wąskim gardle zmienność jest mniejsza, a o tym, kto przeżył, decyduje przypadek, nie przystosowanie.', 'W testach autora katastrofa zabijająca 90% populacji nie obniżyła różnorodności po kolejnych 10 tys. ticków (5 seedów): ok. 70 ocalałych miało dość zmienności, a mutacje szybko ją uzupełniły. Silniejszych katastrof nie sprawdzano.'],
    see: ['event.cull', 'drift', 'diversity'],
  },
  hitchhiking: {
    group: 'Ewolucja',
    title: 'Autostop genetyczny (hitchhiking)',
    short: 'Gen obojętny lub lekko szkodliwy rozprzestrzenia się, bo leży w genomie obok genu bardzo korzystnego.',
    more: ['W rozmnażaniu bezpłciowym cały genom dziedziczy się razem, więc wszystko, co „podróżuje” z udanym mózgiem, też się szerzy. Dlatego cechy o słabym efekcie (np. odporność na zimno w ciepłym świecie) potrafią bez wyraźnego powodu rosnąć lub maleć, a wyniki pojedynczego seeda są zaszumione.'],
    fun: 'Termin wprowadzili John Maynard Smith i John Haigh w 1974 r. Rozmnażanie płciowe osłabia ten efekt, bo geny są tasowane.',
    see: ['clonal', 'sexual', 'series'],
  },
  clonal: {
    group: 'Ewolucja',
    title: 'Interferencja klonalna',
    short: 'W populacji bezpłciowej konkurują całe linie klonów: korzystna mutacja w jednej linii nie może „przeskoczyć” do innej.',
    more: ['Skutek: ewolucja jest wolniejsza i bardziej losowa, a różne linie mogą wygrywać w kolejnych przebiegach.'],
    fun: 'Zjawisko opisali m.in. Philip Gerrish i Richard Lenski (1998) na bakteriach.',
    see: ['hitchhiking', 'sexual'],
  },
  clade: {
    group: 'Ewolucja',
    title: 'Klad',
    short: 'Wszyscy potomkowie jednego założyciela. Kolor „Klad” na planszy pokazuje, czyje potomstwo ma jaką część świata.',
    more: ['Kladów jest na początku tyle, ilu założycieli. Z czasem większość wymiera — zostaje jeden lub kilka. W statystykach widać udział największego kladu i liczbę żyjących.', 'W rozmnażaniu płciowym organizm dziedziczy klad po pierwszym rodzicu, więc to pojęcie jest tu przybliżone.'],
    try: 'Ustaw kolor „Klad”. Obserwuj, jak ewoluują granice terytoriów poszczególnych linii.',
    see: ['sweep', 'diversity', 'lineage'],
  },
  sweep: {
    group: 'Ewolucja',
    title: 'Wymiatanie',
    short: 'Jeden klad (lub gen) przejmuje niemal całą populację. W dzienniku zdarzeń pojawia się wpis, gdy jeden klad przekracza 50% i 90%.',
    more: ['Wymiatanie następuje, gdy linia zdobywa przewagę (lub ma szczęście) i wypiera pozostałe. Po nim różnorodność maleje, więc dalsza ewolucja może zwolnić.'],
    see: ['clade', 'diversity', 'drift'],
  },
  diversity: {
    group: 'Ewolucja',
    title: 'Różnorodność genetyczna',
    short: 'Jak bardzo organizmy różnią się cechami. Liczona jako średnie odchylenie standardowe cech funkcjonalnych (0 = wszyscy identyczni).',
    more: ['Różnorodność to „paliwo” doboru: bez wariantów nie ma z czego wybierać. Spada przy dryfie, wąskim gardle i wymiataniu, rośnie przy mutacjach, rozmnażaniu płciowym i niszach.', 'Obok jest też „odległość genomów” — średnia różnica dla par organizmów, wliczająca wagi sieci.'],
    see: ['drift', 'sweep', 'niche'],
  },
  genomedist: {
    group: 'Ewolucja',
    title: 'Odległość genomów',
    short: 'Średnia (RMS) różnica między genomami losowo dobranych par organizmów, liczona po wszystkich 289 genach.',
    see: ['diversity'],
  },
  niche: {
    group: 'Ewolucja',
    title: 'Nisza ekologiczna',
    short: 'Sposób życia w danym środowisku: czym się żywić, gdzie, w jakim rozmiarze i jak unikać wrogów.',
    more: ['Różne cechy wygrywają w różnych niszach. Gdy w świecie jest kilka dobrych strategii (np. mały roślinożerca i duży drapieżnik), populacja zachowuje większą zmienność.'],
    see: ['exclusion', 'tradeoff', 'strategies'],
  },
  exclusion: {
    group: 'Ewolucja',
    title: 'Zasada konkurencyjnego wykluczania',
    short: 'Dwa gatunki konkurujące o ten sam zasób zwykle nie współistnieją długo: wygrywa ten, który przeżyje przy niższym poziomie zasobu.',
    more: ['Dlatego w świecie bez drapieżników rozmiar spada: wygrywają organizmy o najniższym progu przeżycia, czyli małe i tanie. Współistnienie wymaga czegoś więcej — np. drapieżnika, który hamuje najlepszego konkurenta.'],
    fun: 'Zasadę kojarzy się z doświadczeniami Georgija Gausego z lat 30. XX w. na pantofelkach (Paramecium).',
    see: ['niche', 'trophic', 'allometry'],
  },
  landscape: {
    group: 'Ewolucja',
    title: 'Krajobraz przystosowania',
    short: 'Wyobraź sobie mapę, na której wysokość to sukces rozrodczy, a położenie to genom. Ewolucja wspina się po zboczach, ale może utknąć na lokalnym szczycie.',
    more: ['W GENESIS krajobraz zmienia się razem z populacją: gdy ewoluują drapieżniki, zmienia się „teren” dla ofiar. To dlatego różne seedy kończą w różnych miejscach.'],
    fun: 'Metaforę wprowadził Sewall Wright w 1932 r.',
    see: ['arms', 'drift'],
  },
  arms: {
    group: 'Ewolucja',
    title: 'Wyścig zbrojeń i Czerwona Królowa',
    short: 'Gdy drapieżnik i ofiara ewoluują razem, każda poprawa jednej strony wymusza poprawę drugiej — i żadna nie zyskuje trwałej przewagi.',
    more: ['W GENESIS widać to jako długotrwałe współistnienie drapieżników i roślinożerców, ale też jako ryzyko wymarcia drapieżników, jeśli ofiary zbytnio „uciekną”.'],
    fun: 'Hipotezę Czerwonej Królowej sformułował Leigh Van Valen w 1973 r. Nazwa pochodzi z „Alicji w Krainie Czarów”: trzeba biec, żeby zostać w miejscu.',
    see: ['predation', 'landscape', 'absorbing'],
  },
  absorbing: {
    group: 'Ewolucja',
    title: 'Stan pochłaniający',
    short: 'Sytuacja, z której system już nie wraca. Wymarcie całej populacji jest takie; brak drapieżników nie jest formalnie nieodwracalny, ponieważ gen diety nadal mutuje.',
    more: ['W jednym z przebiegów na milion ticków (seed 5) drapieżnicy wymarli między 700 a 900 tys. ticków. Potem gen diety spadł do zera i w obserwowanym przebiegu zdolność ataku nie powróciła, a populacja zbiegła do małych, prawie ślepych roślinożerców.'],
    see: ['arms', 'exclusion'],
  },
  emergence: {
    group: 'Ewolucja',
    title: 'Emergencja (zjawiska wyłaniające się)',
    short: 'Zachowania i wzorce, których nikt nie zaprogramował, a które wynikają z prostych reguł i ich współdziałania.',
    more: [
      'Narzucone są prawa świata: koszty, wzrost, transfer energii przy ataku. Wyłania się to, co z tym zrobią organizmy: czy ścigają pokarm, czy uciekają, czy pamiętają, czy drapieżnictwo się utrzyma.',
      'Dlatego każdą obserwację warto sprawdzić (sondy, ablacja, wiele seedów), zamiast ufać wrażeniu.',
    ],
    fun: 'Podobny pomysł ma „Gra w życie” Johna Conwaya z 1970 r.: kilka prostych reguł, a na planszy pojawiają się „szybowce” i „działa”.',
    see: ['evolution', 'probes', 'ablation'],
  },
  lineage: {
    group: 'Ewolucja',
    title: 'Genealogia (rodowód)',
    short: 'Drzewo rodzinne: kto jest czyim rodzicem. Pozwala prześledzić organizm do założyciela i porównać jego genom z genomem przodka.',
    more: [
      'Dla każdego organizmu pamiętane są: rodzice, pokolenie, narodziny, śmierć, liczba dzieci, cechy przy narodzinach i lista mutacji. Pełny genom przodka odtwarza się z genomu założyciela i kolejnych list mutacji — nie przechowuje się całych genomów.',
      'Żeby pamięć nie rosła bez końca, martwe linie bez żywych potomków są przycinane po 20 tys. ticków, a długie łańcuchy przodków bez rozgałęzień są scalane. Przodkowie żywych organizmów nigdy nie znikają.',
    ],
    try: 'Zaznacz organizm z odległego pokolenia i otwórz zakładkę Genealogia, a potem przesuń suwak przodka, żeby zobaczyć, które cechy się zmieniły.',
    see: ['clade', 'mutation', 'generation'],
  },

  // ————————————————————————————— EKOLOGIA —————————————————————————————
  predation: {
    group: 'Ekologia',
    title: 'Drapieżnictwo',
    short: 'Organizm z dietą > 0,02 może odebrać energię innemu organizmowi w kontakcie. Atak jest zdolnością; to sieć decyduje, czy i kiedy go użyć.',
    more: [
      'Napastnik wybiera najbliższy organizm w zasięgu dotyku i odbiera mu energię proporcjonalnie do wysiłku, diety i siły. Zysk to 85% × dieta zabranej energii, reszta rozprasza się jako ciepło. Ofiara wyczerpana do zera ginie.',
      'Gdy kilku napastników atakuje tę samą ofiarę, dzielą ją po równo (a nie wg numerów w pamięci). Kanibalizm i atak na własne potomstwo są możliwe — nic ich nie zabrania.',
    ],
    fun: 'W przebiegach autora drapieżnicy (ok. 9–25% populacji) trwali tysiące pokoleń, ale w jednym przebiegu na milion ticków wymarli.',
    try: 'Porównaj scenariusz „Bez drapieżników” z domyślnym.',
    see: ['trait.diet', 'trophic', 'arms', 'lotka'],
  },
  trophic: {
    group: 'Ekologia',
    title: 'Kaskada troficzna',
    short: 'Drapieżnik przetrzebia ofiary, więc rośnie to, czym te się żywią. Zmiana na jednym poziomie przenosi się na inne.',
    more: ['W GENESIS widać to wprost: z drapieżnikami roślinożercy nie zdążą wyjeść pokarmu do zera — zasoby stoją na 15–80% pojemności zamiast 3–5%, bo ofiary są przetrzebione.', 'Energia traci się na każdym szczeblu łańcucha pokarmowego (straty trawienia i ruchu).'],
    fun: 'Klasyczne przykłady z natury to wydry morskie, kelp i jeżowce (Estes i Palmisano, 1974) oraz często omawiane — choć dyskutowane — wilki w Yellowstone.',
    see: ['predation', 'resources', 'exclusion'],
  },
  lotka: {
    group: 'Ekologia',
    title: 'Cykle drapieżnik–ofiara',
    short: 'Liczba ofiar rośnie, więc rośnie liczba drapieżników, które ofiary zjadają, więc ofiar ubywa, więc drapieżniki głodują — i od nowa.',
    more: ['Opisują to równania Lotki–Volterry (1925–26). W GENESIS cykle są zaszumione i zmieniają się razem z ewoluującymi cechami, więc nie wyglądają jak podręcznikowe sinusoidy.'],
    fun: 'Klasycznym przykładem jest wieloletni cykl rysi i zajęcy w Kanadzie, odtwarzany z zapisów handlarzy futer.',
    see: ['predation', 'arms'],
  },
  competition: {
    group: 'Ekologia',
    title: 'Konkurencja o pokarm',
    short: 'Gdy kilka organizmów je w tej samej komórce, dzielą jej zasób proporcjonalnie do apetytu i (domyślnie) promienia ciała.',
    more: ['Większe organizmy wygrywają w bezpośredniej rywalizacji o ten sam kęs, ale płacą wyższe koszty życia.'],
    see: ['param.contestPower', 'allometry'],
  },
  herbivore: {
    group: 'Ekologia',
    title: 'Roślinożerca, mięsożerca i wszystkożerca',
    short: 'Rolę określa gen diety: 0 = czysty roślinożerca, 1 = drapieżnik. Wartości pośrednie to wszystkożercy z kompromisem.',
    see: ['trait.diet', 'predation'],
  },
  tragedy: {
    group: 'Ekologia',
    title: 'Tragedia wspólnego pastwiska',
    short: 'Każdy wyjada ile może, bo gdyby się powstrzymał, zyskałby tylko konkurent. Wspólny zasób zostaje zdewastowany.',
    more: ['Dlatego w GENESIS przeżarte komórki odnawiają się wolno: opłaca się szukać nietkniętych miejsc, a oszczędność ma sens tylko wtedy, gdy rośnie z niej przewaga.'],
    fun: 'Pojęcie spopularyzował Garrett Hardin esejem z 1968 r.',
    see: ['logistic', 'competition'],
  },
};
